import { useNavigate } from "@tanstack/react-router";
import { saveWorkoutItemAsSection } from "api-client";
import { useTranslation } from "react-i18next";
import { useToast } from "ui";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { SavedSectionToastDescription } from "./SavedSectionToastDescription";
import { SavedSectionToastTitle } from "./SavedSectionToastTitle";
import { useWorkoutEditStore } from "./WorkoutEditContext";

interface UseSaveWorkoutItemAsSectionInput {
  workoutItemId: string;
}

export function useSaveWorkoutItemAsSection(
  input: UseSaveWorkoutItemAsSectionInput,
) {
  const workoutEditStore = useWorkoutEditStore();
  const authenticatedNavigationContext = useAuthenticatedNavigationContext();
  const toast = useToast();
  const navigate = useNavigate();
  const translation = useTranslation();

  async function saveAsSection() {
    const stop = authenticatedNavigationContext.startSaving();
    const sectionName = workoutEditStore
      .getWorkoutItemName(input.workoutItemId)
      .trim();

    try {
      const savedSectionName =
        sectionName || translation.t("sectionsPage.newSectionName");

      const response = await saveWorkoutItemAsSection({
        path: {
          workoutId: workoutEditStore.workout.id,
          workoutItemId: input.workoutItemId,
        },
        body: {
          name: savedSectionName,
        },
      });

      stop();

      function dismissToast() {
        createdToast.dismiss();
      }

      function showSection() {
        createdToast.dismiss();
        navigate({
          to: "/library/sections",
          search: {
            sectionId: response.data.data.sectionId,
          },
        });
      }

      const createdToast = toast.toast({
        title: <SavedSectionToastTitle sectionName={savedSectionName} />,
        description: (
          <SavedSectionToastDescription
            onDismiss={dismissToast}
            onShowSection={showSection}
          />
        ),
        className:
          "items-start rounded-lg border-zinc-200 bg-white p-3 pr-9 text-zinc-950 shadow-lg",
        variant: "default",
      });
    } catch (error) {
      stop("error");
      toast.toast({
        title: translation.t(
          "editWorkout.workoutItemActions.saveAsSectionError",
        ),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    }
  }

  return saveAsSection;
}
